import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader.js';

/*
 * PyooLab landing scene.
 *
 * The scene geometry lives in public/models/lab.glb and its lighting is
 * fully baked (Blender Cycles) into two atlases: lab_bake_day.jpg and
 * lab_bake_night.jpg. Meshes render unlit, sampling both atlases and
 * crossfading between them with a shared uNight uniform — offline render
 * quality plus a day/night cycle, all at 60fps.
 * Regenerate assets with: blender --background --python scripts/build_lab.py -- bake
 */

// Sky-matched clear colors (day haze / night navy) for the crossfade
const DAY_BG = new THREE.Color(0xa7bfd4);
const NIGHT_BG = new THREE.Color(0x060a14);

// Blender is Z-up, glTF/Three is Y-up: (x, y, z)_blender -> (x, z, -y)_three
const CAMERA_HOME = new THREE.Vector3(0.15, 1.6, 2.6);
const CAMERA_TARGET = new THREE.Vector3(-0.05, 0.95, -0.6);
const EYE_HEIGHT = 1.55;

// Routes point into the current site: the tabbed homepage ("/") with an
// optional ?tab, or a mailto. Keep these in sync with src/pages/Home.jsx.
const HOTSPOTS = [
  { match: 'hot_laptop', label: 'Professional', hint: 'Experience & work', route: '/' },
  { match: 'hot_scope', label: 'Projects', hint: 'Things I have built', route: '/' },
  { match: 'hot_notebook', label: 'Posts', hint: 'Writing & notes', route: '/?tab=personal' },
  { match: 'hot_mug', label: 'Contact', hint: 'Say hi', route: 'mailto:inisgood@gmail.com' },
  { match: 'hot_pipette', label: 'Personal', hint: 'Life outside the lab', route: '/?tab=personal' },
];

const IS_TOUCH = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

// Day/night cycle timing (seconds)
const DAY_HOLD = 20;
const FADE = 5;
const CYCLE = (DAY_HOLD + FADE) * 2;

// 0 = day, 1 = night, with smooth eased fades between holds
function autoNightValue(t) {
  const p = t % CYCLE;
  if (p < DAY_HOLD) return 0;
  if (p < DAY_HOLD + FADE) return THREE.MathUtils.smoothstep((p - DAY_HOLD) / FADE, 0, 1);
  if (p < DAY_HOLD + FADE + DAY_HOLD) return 1;
  return 1 - THREE.MathUtils.smoothstep((p - DAY_HOLD - FADE - DAY_HOLD) / FADE, 0, 1);
}

// Walkable rectangle and furniture blockers, in Three.js world space (x, z)
const WALK_BOUNDS = { minX: -2.15, maxX: 2.35, minZ: -2.1, maxZ: 2.4 };
const BLOCKERS = [
  { minX: -1.0, maxX: 1.0, minZ: -0.95, maxZ: 0.2 },   // desk
  { minX: -1.75, maxX: -0.95, minZ: 0.45, maxZ: 1.25 }, // chair
  { minX: 2.0, maxX: 3.2, minZ: -2.5, maxZ: 2.1 },      // bench
  { minX: -3.2, maxX: -2.2, minZ: -1.6, maxZ: 0.4 },    // fume hood
];

function collides(x, z) {
  const r = 0.25;
  return BLOCKERS.some(
    (b) => x > b.minX - r && x < b.maxX + r && z > b.minZ - r && z < b.maxZ + r
  );
}

export default function LabLanding({ onEnter, darkMode }) {
  const mountRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [walkMode, setWalkMode] = useState(false);

  const walkModeRef = useRef(false);
  const keysRef = useRef({});
  const stateRef = useRef(null); // camera/scene handles for the walk toggle

  // Day/night crossfade. nightRef doubles as the shared shader uniform.
  const startAtNight =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('night') === '1';
  const nightRef = useRef({ value: startAtNight ? 1 : 0 });
  // null = auto cycle; 0/1 = user-pinned day/night
  const manualTargetRef = useRef(startAtNight ? 1 : null);
  const [isNightUI, setIsNightUI] = useState(startAtNight);

  useEffect(() => {
    document.body.classList.toggle('dark-bg', darkMode);
    document.body.classList.toggle('light-bg', !darkMode);
  }, [darkMode]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0e1116);

    const camera = new THREE.PerspectiveCamera(
      55,
      window.innerWidth / window.innerHeight,
      0.05,
      130 // the 45m sky dome + ground must never clip
    );
    camera.position.copy(CAMERA_HOME);
    camera.lookAt(CAMERA_TARGET);

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Bake is already Filmic tone-mapped in Blender; don't grade it twice.
    renderer.toneMapping = THREE.NoToneMapping;
    mount.appendChild(renderer.domElement);

    // Post-processing: subtle bloom (window/screens glow) + vignette for a
    // photographic, less "flat render" feel. Bloom is gentle in day, stronger
    // at night; the vignette is constant. OutputPass handles final sRGB.
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    composer.setSize(window.innerWidth, window.innerHeight);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.35, // strength (day baseline; nudged up at night in the loop)
      0.7, // radius
      0.9 // threshold — high enough that the daytime sky doesn't smear
    );
    composer.addPass(bloomPass);
    const vignettePass = new ShaderPass(VignetteShader);
    vignettePass.uniforms.offset.value = 1.1;
    vignettePass.uniforms.darkness.value = 1.1;
    composer.addPass(vignettePass);
    composer.addPass(new OutputPass());

    // Small environment map so real-time glass picks up reflections.
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTex;

    const glassMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.05,
      metalness: 0,
      transparent: true,
      opacity: 0.18,
      envMapIntensity: 1.2,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const hotspotMeshes = [];
    const disposables = [glassMaterial];

    const texLoader = new THREE.TextureLoader();
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    const loadBake = (path) => {
      const t = texLoader.load(path);
      t.flipY = false; // glTF UV convention
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = maxAniso; // sharp at grazing angles (floor, arcs, dome)
      disposables.push(t);
      return t;
    };
    const dayTex = loadBake('/models/lab_bake_day.jpg');
    const nightTex = loadBake('/models/lab_bake_night.jpg');

    // One uniform shared by every baked material drives the global crossfade
    const nightUniform = nightRef.current;

    // ---- outdoor imagery (not baked: textured directly for sharpness) ----
    // Sky dome: real photographic skies (Poly Haven, Real-ESRGAN enhanced),
    // cropped to the front 180° above the horizon. The dome's UVs (built in
    // Blender) map that sector 1:1, so every texture pixel lands where the
    // window actually looks; beyond the sector the texture clamps to its
    // edges (behind the room / below the ground — never visible).
    const skyDayTex = loadBake('/models/sky_day.jpg');
    const skyNightTex = loadBake('/models/sky_night.jpg');
    const skyMaterial = new THREE.MeshBasicMaterial({ map: skyDayTex });
    skyMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.tNight = { value: skyNightTex };
      shader.uniforms.uNight = nightUniform;
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          '#include <common>\nuniform sampler2D tNight;\nuniform float uNight;'
        )
        .replace(
          '#include <map_fragment>',
          [
            'vec4 skyDay = texture2D( map, vMapUv );',
            'vec4 skyNight = texture2D( tNight, vMapUv );',
            'diffuseColor *= mix( skyDay, skyNight, uNight );',
          ].join('\n')
        );
    };
    skyMaterial.customProgramCacheKey = () => 'sky-day-night';
    disposables.push(skyMaterial);

    // Parallax billboard arcs (near pines / mid foothills / far peaks):
    // day imagery fading to cool silhouettes as uNight rises.
    const makeLayerMaterial = (path) => {
      const m = new THREE.MeshBasicMaterial({
        map: loadBake(path),
        transparent: true,
        alphaTest: 0.06,
        side: THREE.DoubleSide,
      });
      m.onBeforeCompile = (shader) => {
        shader.uniforms.uNight = nightUniform;
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform float uNight;')
          .replace(
            '#include <map_fragment>',
            '#include <map_fragment>\ndiffuseColor.rgb *= mix( vec3(1.0), vec3(0.10, 0.13, 0.22), uNight );'
          );
      };
      m.customProgramCacheKey = () => 'layer-night-tint';
      disposables.push(m);
      return m;
    };
    const layerMaterials = {
      bb_near: makeLayerMaterial('/models/layer_near.webp'),
      bb_mid: makeLayerMaterial('/models/layer_mid.webp'),
      bb_far: makeLayerMaterial('/models/layer_far.webp'),
    };

    // Full-resolution overlay planes: poster art and monitor screen content
    // are textured directly (not through the baked atlas), so they render at
    // native image resolution. Posters dim at night; the screen stays lit.
    const makeOverlayMaterial = (path, nightTint) => {
      const m = new THREE.MeshBasicMaterial({
        map: loadBake(path),
        side: THREE.DoubleSide,
      });
      if (nightTint) {
        m.onBeforeCompile = (shader) => {
          shader.uniforms.uNight = nightUniform;
          shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', '#include <common>\nuniform float uNight;')
            .replace(
              '#include <map_fragment>',
              `#include <map_fragment>\ndiffuseColor.rgb *= mix( vec3(1.0), vec3(${nightTint}), uNight );`
            );
        };
        m.customProgramCacheKey = () => `overlay-${nightTint}`;
      }
      disposables.push(m);
      return m;
    };
    const overlayMaterials = {
      img_poster1: makeOverlayMaterial('/models/poster_blue.jpg', '0.32, 0.34, 0.42'),
      img_poster2: makeOverlayMaterial('/models/poster_sand.jpg', '0.32, 0.34, 0.42'),
      img_screen: makeOverlayMaterial('/models/screen_monitor.jpg', null),
    };

    const makeBakedMaterial = () => {
      const m = new THREE.MeshBasicMaterial({ map: dayTex });
      m.onBeforeCompile = (shader) => {
        shader.uniforms.tNight = { value: nightTex };
        shader.uniforms.uNight = nightUniform;
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            '#include <common>\nuniform sampler2D tNight;\nuniform float uNight;'
          )
          .replace(
            '#include <map_fragment>',
            [
              'vec4 texDay = texture2D( map, vMapUv );',
              'vec4 texNight = texture2D( tNight, vMapUv );',
              'diffuseColor *= mix( texDay, texNight, uNight );',
            ].join('\n')
          );
      };
      m.customProgramCacheKey = () => 'baked-day-night';
      disposables.push(m);
      return m;
    };
    const bakedMaterial = makeBakedMaterial();

    const loader = new GLTFLoader();
    loader.load(
      '/models/lab.glb',
      (gltf) => {
        gltf.scene.traverse((child) => {
          if (!child.isMesh) return;
          if (child.name.startsWith('glass')) {
            child.material = glassMaterial;
            child.renderOrder = 1;
          } else if (child.name === 'sky') {
            child.material = skyMaterial;
          } else if (layerMaterials[child.name]) {
            child.material = layerMaterials[child.name];
          } else if (overlayMaterials[child.name]) {
            child.material = overlayMaterials[child.name];
          } else {
            child.material = bakedMaterial;
          }
          const spot = HOTSPOTS.find((h) => child.name.startsWith(h.match));
          if (spot) {
            // Own material instance so hover highlight doesn't tint the room
            child.material = makeBakedMaterial();
            child.userData.hotspot = spot;
            hotspotMeshes.push(child);
          }
        });
        scene.add(gltf.scene);
        setReady(true);
      },
      (xhr) => {
        if (xhr.total > 0) setProgress(Math.round((xhr.loaded / xhr.total) * 100));
      },
      (err) => {
        console.error('Failed to load lab scene:', err);
        setLoadError(true);
      }
    );

    // ---------------------------------------------------------- interaction
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const parallax = new THREE.Vector2();
    let hoveredMesh = null;
    const lookEuler = new THREE.Euler(0, 0, 0, 'YXZ');

    const setHighlight = (mesh, on) => {
      const group = mesh ? mesh.userData.hotspot : null;
      hotspotMeshes.forEach((m) => {
        const active = on && group && m.userData.hotspot === group;
        m.material.color.setScalar(active ? 1.5 : 1.0);
      });
    };

    const onPointerMove = (e) => {
      if (walkModeRef.current) {
        if (document.pointerLockElement === renderer.domElement) {
          lookEuler.setFromQuaternion(camera.quaternion);
          lookEuler.y -= (e.movementX || 0) * 0.0022;
          lookEuler.x -= (e.movementY || 0) * 0.0022;
          lookEuler.x = Math.max(-1.4, Math.min(1.4, lookEuler.x));
          camera.quaternion.setFromEuler(lookEuler);
        }
        return;
      }
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
      parallax.set(pointer.x, pointer.y);

      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(hotspotMeshes, false);
      const mesh = hits.length ? hits[0].object : null;
      if (mesh !== hoveredMesh) {
        hoveredMesh = mesh;
        setHighlight(mesh, !!mesh);
        setHovered(mesh ? mesh.userData.hotspot : null);
        renderer.domElement.style.cursor = mesh ? 'pointer' : 'default';
      }
    };

    const onClick = (e) => {
      if (walkModeRef.current) {
        raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
        const hits = raycaster.intersectObjects(hotspotMeshes, false);
        if (hits.length) setSelected(hits[0].object.userData.hotspot);
        return;
      }
      // Raycast at the click position itself so taps work on touch devices
      // (no pointermove happens before a tap).
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(hotspotMeshes, false);
      setSelected(hits.length ? hits[0].object.userData.hotspot : null);
    };

    const onKey = (down) => (e) => {
      keysRef.current[e.code] = down;
    };
    const keyDown = onKey(true);
    const keyUp = onKey(false);

    const onPointerLockChange = () => {
      if (!document.pointerLockElement && walkModeRef.current) {
        exitWalk();
      }
    };

    // ---------------------------------------------------------- walk mode
    const enterWalk = () => {
      walkModeRef.current = true;
      setWalkMode(true);
      setHovered(null);
      setHighlight(null, false);
      camera.position.set(0.15, EYE_HEIGHT, 2.2);
      camera.lookAt(CAMERA_TARGET.x, EYE_HEIGHT - 0.15, CAMERA_TARGET.z);
      renderer.domElement.requestPointerLock?.();
    };
    const exitWalk = () => {
      walkModeRef.current = false;
      setWalkMode(false);
      keysRef.current = {};
      if (document.pointerLockElement) document.exitPointerLock();
      camera.position.copy(CAMERA_HOME);
      camera.lookAt(CAMERA_TARGET);
    };
    stateRef.current = { enterWalk, exitWalk };

    // ---------------------------------------------------------- loop
    const clock = new THREE.Clock();
    const basePos = CAMERA_HOME.clone();
    let rafId;
    let cycleTime = nightRef.current.value > 0.5 ? DAY_HOLD + FADE : 0;
    let lastNightFlag = nightRef.current.value > 0.5;

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05);

      // Day/night: auto-cycle until the user pins a phase, then ease to it
      const manual = manualTargetRef.current;
      if (manual === null) {
        cycleTime += dt;
        nightRef.current.value = autoNightValue(cycleTime);
      } else {
        const v = nightRef.current.value;
        nightRef.current.value = v + (manual - v) * Math.min(dt * 1.6, 1);
      }
      const nowNight = nightRef.current.value > 0.5;
      if (nowNight !== lastNightFlag) {
        lastNightFlag = nowNight;
        setIsNightUI(nowNight);
      }
      // Warmer, stronger bloom after dark (lamp + screens carry the room)
      bloomPass.strength = 0.3 + nightRef.current.value * 0.5;
      // Background tracks the sky so any sliver outside geometry never reads black
      scene.background.copy(DAY_BG).lerp(NIGHT_BG, nightRef.current.value);

      if (walkModeRef.current) {
        const k = keysRef.current;
        const speed = 2.2;
        const fwd = new THREE.Vector3();
        camera.getWorldDirection(fwd);
        fwd.y = 0;
        fwd.normalize();
        const right = new THREE.Vector3().crossVectors(fwd, camera.up).normalize();
        const move = new THREE.Vector3();
        if (k.KeyW || k.ArrowUp) move.add(fwd);
        if (k.KeyS || k.ArrowDown) move.sub(fwd);
        if (k.KeyD || k.ArrowRight) move.add(right);
        if (k.KeyA || k.ArrowLeft) move.sub(right);
        if (move.lengthSq() > 0) {
          move.normalize().multiplyScalar(speed * dt);
          const nx = THREE.MathUtils.clamp(camera.position.x + move.x, WALK_BOUNDS.minX, WALK_BOUNDS.maxX);
          const nz = THREE.MathUtils.clamp(camera.position.z + move.z, WALK_BOUNDS.minZ, WALK_BOUNDS.maxZ);
          if (!collides(nx, camera.position.z)) camera.position.x = nx;
          if (!collides(camera.position.x, nz)) camera.position.z = nz;
          camera.position.y = EYE_HEIGHT;
        }
      } else {
        // Gentle parallax drift toward the pointer
        const tx = basePos.x + parallax.x * 0.22;
        const ty = basePos.y + parallax.y * 0.12;
        camera.position.x += (tx - camera.position.x) * Math.min(dt * 3, 1);
        camera.position.y += (ty - camera.position.y) * Math.min(dt * 3, 1);
        camera.position.z = basePos.z;
        camera.lookAt(CAMERA_TARGET);
      }

      composer.render();
    };
    animate();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      composer.setSize(window.innerWidth, window.innerHeight);
    };

    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('click', onClick);
    document.addEventListener('keydown', keyDown);
    document.addEventListener('keyup', keyUp);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('click', onClick);
      document.removeEventListener('keydown', keyDown);
      document.removeEventListener('keyup', keyUp);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      window.removeEventListener('resize', onResize);
      if (document.pointerLockElement) document.exitPointerLock();

      scene.traverse((child) => {
        if (child.isMesh) child.geometry?.dispose();
      });
      disposables.forEach((d) => d.dispose?.());
      envTex.dispose();
      pmrem.dispose();
      composer.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  const toggleWalk = useCallback(() => {
    if (!stateRef.current) return;
    if (walkModeRef.current) stateRef.current.exitWalk();
    else stateRef.current.enterWalk();
  }, []);

  const toggleDayNight = useCallback(() => {
    // Pin to the opposite of the current phase; auto-cycling stops here
    const target = nightRef.current.value > 0.5 ? 0 : 1;
    manualTargetRef.current = target;
    setIsNightUI(target === 1);
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#0e1116]">
      <div ref={mountRef} className="w-full h-full" />

      {/* Loading */}
      {!ready && !loadError && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0e1116]">
          <div className="text-center w-64">
            <h2 className="text-gray-200 text-xl font-semibold tracking-widest mb-6">PYOOLAB</h2>
            <div className="h-1 w-full bg-gray-800 rounded overflow-hidden">
              <div
                className="h-full bg-blue-400 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs mt-3">preparing the lab… {progress}%</p>
          </div>
        </div>
      )}

      {loadError && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0e1116]">
          <div className="text-center">
            <p className="text-gray-300 mb-4">The 3D lab could not be loaded.</p>
            <button
              onClick={() => onEnter()}
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 px-6 rounded-lg"
            >
              Continue to portfolio →
            </button>
          </div>
        </div>
      )}

      {ready && (
        <>
          {/* Hint line (title removed) */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10 text-center pointer-events-none select-none">
            <p className="text-gray-300 text-xs md:text-sm tracking-wide drop-shadow">
              {walkMode
                ? 'WASD to move · aim at an object and click · ESC to exit'
                : 'hover the desk, hood & bench — click an object to explore'}
            </p>
          </div>

          {/* Crosshair in walk mode */}
          {walkMode && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
              <div className="w-2 h-2 rounded-full bg-white/80 shadow" />
            </div>
          )}

          {/* Hover tooltip */}
          {hovered && !walkMode && (
            <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
              <div className="bg-black/75 backdrop-blur text-white px-5 py-2.5 rounded-full shadow-xl border border-white/10 text-center">
                <span className="font-semibold">{hovered.label}</span>
                <span className="text-gray-400 text-sm ml-2">{hovered.hint}</span>
              </div>
            </div>
          )}

          {/* Selection panel */}
          {selected && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
              <div className="bg-black/80 backdrop-blur text-white px-6 py-4 rounded-2xl shadow-2xl border border-white/10 flex items-center gap-4">
                <div>
                  <p className="font-bold">{selected.label}</p>
                  <p className="text-gray-400 text-sm">{selected.hint}</p>
                </div>
                <button
                  onClick={() => onEnter(selected.route)}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 px-5 rounded-lg transition-colors"
                >
                  Enter →
                </button>
                <button
                  onClick={() => setSelected(null)}
                  className="text-gray-400 hover:text-white text-xl leading-none px-1"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Controls: day/night + walk toggle (desktop only) + skip */}
          <div className="absolute bottom-6 right-6 z-10 flex gap-3">
            <button
              onClick={toggleDayNight}
              className="bg-white/10 hover:bg-white/20 backdrop-blur text-white text-sm font-medium py-2 px-4 rounded-lg border border-white/15 transition-colors"
            >
              {isNightUI ? 'Switch to day' : 'Switch to night'}
            </button>
            {!IS_TOUCH && (
              <button
                onClick={toggleWalk}
                className="bg-white/10 hover:bg-white/20 backdrop-blur text-white text-sm font-medium py-2 px-4 rounded-lg border border-white/15 transition-colors"
              >
                {walkMode ? 'Exit walk mode' : 'Walk around'}
              </button>
            )}
            <button
              onClick={() => onEnter()}
              className="bg-white/10 hover:bg-white/20 backdrop-blur text-white text-sm font-medium py-2 px-4 rounded-lg border border-white/15 transition-colors"
            >
              Skip intro →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
